import { useCallback, useRef, useState } from 'react'

import { loadGoogleMaps } from '@/lib/googleMaps'

interface Prediction {
  id: string
  description: string
}

interface AddressComponents {
  line1: string
  city: string
  state: string
  zip: string
}

function findComponent(components: google.maps.GeocoderAddressComponent[], type: string, short = false) {
  const c = components.find((c) => c.types.includes(type))
  return c ? (short ? c.short_name : c.long_name) : ''
}

function parseAddressComponents(components: google.maps.GeocoderAddressComponent[]): AddressComponents {
  const streetNumber = findComponent(components, 'street_number')
  const route = findComponent(components, 'route')
  return {
    line1: [streetNumber, route].filter(Boolean).join(' '),
    city: findComponent(components, 'locality') || findComponent(components, 'sublocality') || '',
    state: findComponent(components, 'administrative_area_level_1', true),
    zip: findComponent(components, 'postal_code'),
  }
}

export function useAddressAutocomplete() {
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null)
  const placesRef = useRef<google.maps.places.PlacesService | null>(null)
  const tokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)
  const requestSeqRef = useRef(0)

  const search = useCallback((input: string) => {
    if (!input || input.length < 3) {
      setPredictions([])
      return
    }

    const seq = ++requestSeqRef.current

    loadGoogleMaps()
      .then(() => {
        if (seq !== requestSeqRef.current) return
        serviceRef.current ??= new google.maps.places.AutocompleteService()
        tokenRef.current ??= new google.maps.places.AutocompleteSessionToken()

        serviceRef.current.getPlacePredictions(
          {
            input,
            types: ['address'],
            componentRestrictions: { country: 'us' },
            sessionToken: tokenRef.current,
          },
          (results, status) => {
            if (seq !== requestSeqRef.current) return
            if (status !== google.maps.places.PlacesServiceStatus.OK || !results) {
              setPredictions([])
              return
            }
            setPredictions(
              results.map((p) => ({
                id: p.place_id,
                description: p.description,
              }))
            )
          }
        )
      })
      .catch(() => {
        if (seq === requestSeqRef.current) setPredictions([])
      })
  }, [])

  const select = useCallback(async (id: string): Promise<AddressComponents | null> => {
    await loadGoogleMaps()
    placesRef.current ??= new google.maps.places.PlacesService(document.createElement('div'))

    return new Promise((resolve) => {
      placesRef.current!.getDetails(
        {
          placeId: id,
          fields: ['address_components'],
          sessionToken: tokenRef.current ?? undefined,
        },
        (place, status) => {
          tokenRef.current = null
          if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.address_components) {
            resolve(null)
            return
          }
          resolve(parseAddressComponents(place.address_components))
        }
      )
    })
  }, [])

  const clear = useCallback(() => {
    setPredictions([])
  }, [])

  return { predictions, search, select, clear }
}
